export interface PlanIndexFilterState {
    allCheckedAttribute: Record<string, Set<string>>;
    allSelectedValueForAllAttribute: Record<string, boolean>;
    applyFilterObject: Record<string, { inclusion?: Set<string>; exclusion?: Set<string> }>;
}

interface SerializedFilterState {
    checked?: Record<string, string[]>;
    selectAll?: Record<string, boolean>;
    apply?: Record<string, { inclusion?: string[]; exclusion?: string[] }>;
}

/** Empty filter state with every configured attribute key initialised. */
export function buildEmptyFilterState(filterKeys: string[]): PlanIndexFilterState {
    const allCheckedAttribute: Record<string, Set<string>> = {};
    const allSelectedValueForAllAttribute: Record<string, boolean> = {};
    const applyFilterObject: Record<string, { inclusion?: Set<string>; exclusion?: Set<string> }> = {};

    filterKeys.forEach((key) => {
        allCheckedAttribute[key] = new Set();
        applyFilterObject[key] = {};
        allSelectedValueForAllAttribute[key] = false;
    });

    return { allCheckedAttribute, allSelectedValueForAllAttribute, applyFilterObject };
}

/**
 * Serialize the filter triplet into a compact, Set-free JSON string for the URL.
 * Empty attributes are dropped; returns null when nothing is active.
 */
export function encodeFilterState(state: PlanIndexFilterState): string | null {
    const checked: Record<string, string[]> = {};
    Object.entries(state.allCheckedAttribute ?? {}).forEach(([key, set]) => {
        const values = set ? Array.from(set) : [];
        if (values.length) checked[key] = values;
    });

    const selectAll: Record<string, boolean> = {};
    Object.entries(state.allSelectedValueForAllAttribute ?? {}).forEach(([key, value]) => {
        if (value) selectAll[key] = true;
    });

    const apply: Record<string, { inclusion: string[]; exclusion: string[] }> = {};
    Object.entries(state.applyFilterObject ?? {}).forEach(([key, value]) => {
        const inclusion = value?.inclusion ? Array.from(value.inclusion) : [];
        const exclusion = value?.exclusion ? Array.from(value.exclusion) : [];
        if (inclusion.length || exclusion.length) apply[key] = { inclusion, exclusion };
    });

    if (!Object.keys(checked).length && !Object.keys(selectAll).length && !Object.keys(apply).length) {
        return null;
    }

    return JSON.stringify({ checked, selectAll, apply });
}

/**
 * Rebuild the filter triplet (with Sets) from the URL string. Falls back to an
 * empty state on missing or malformed input.
 */
export function decodeFilterState(raw: string | null, filterKeys: string[]): PlanIndexFilterState {
    const state = buildEmptyFilterState(filterKeys);
    if (!raw) return state;

    try {
        const parsed = JSON.parse(raw) as SerializedFilterState;

        Object.entries(parsed.checked ?? {}).forEach(([key, values]) => {
            state.allCheckedAttribute[key] = new Set(values ?? []);
        });

        Object.entries(parsed.selectAll ?? {}).forEach(([key, value]) => {
            state.allSelectedValueForAllAttribute[key] = Boolean(value);
        });

        Object.entries(parsed.apply ?? {}).forEach(([key, value]) => {
            state.applyFilterObject[key] = {
                inclusion: new Set(value?.inclusion ?? []),
                exclusion: new Set(value?.exclusion ?? []),
            };
        });

        return state;
    } catch {
        return buildEmptyFilterState(filterKeys);
    }
}

/** Comma-join selected columns; omit (null) when they match the defaults. */
export function encodeColumns(selectedColumns: string[], defaultVisibleColumns: string[]): string | null {
    if (!selectedColumns?.length) return null;

    const isDefault =
        selectedColumns.length === defaultVisibleColumns.length &&
        selectedColumns.every((col) => defaultVisibleColumns.includes(col));

    return isDefault ? null : selectedColumns.join(',');
}

export function decodeColumns(raw: string | null, defaultVisibleColumns: string[]): string[] {
    if (!raw) return defaultVisibleColumns;
    const columns = raw.split(',').filter(Boolean);
    return columns.length ? columns : defaultVisibleColumns;
}
